import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface OnlineUser {
  user_id: string;
  nome: string | null;
  device: 'mobile' | 'desktop';
}

interface PresenceContextValue {
  onlineCount: number;
  onlineUsers: OnlineUser[];
}

const PresenceContext = createContext<PresenceContextValue>({
  onlineCount: 0,
  onlineUsers: [],
});

function detectDevice(): 'mobile' | 'desktop' {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
}

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const [onlineCount, setOnlineCount] = useState(0);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRef = useRef(false);
  const lastBeatRef = useRef(0);

  const refreshCount = async () => {
    const { data, error } = await supabase.rpc('get_online_users_count');
    if (!error && typeof data === 'number') setOnlineCount(data);
  };

  const beat = async (force = false) => {
    if (!activeRef.current) return;
    const now = Date.now();
    if (!force && now - lastBeatRef.current < 1500) return;
    lastBeatRef.current = now;

    const { error } = await supabase.rpc('heartbeat_online_user', {
      p_device: detectDevice(),
    });
    if (!error) await refreshCount();
  };

  const stopHeartbeat = () => {
    activeRef.current = false;
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = null;
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    activeRef.current = true;
    void beat(true);
    heartbeatRef.current = setInterval(() => void beat(), 3000);
  };

  useEffect(() => {
    void refreshCount();
    countRef.current = setInterval(() => void refreshCount(), 5000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) startHeartbeat();
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user?.id) {
        startHeartbeat();
      } else if (event === 'SIGNED_OUT') {
        stopHeartbeat();
        void refreshCount();
      }
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') void beat(true);
    };
    const onFocus = () => void beat(true);
    const onTouch = () => void beat();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    document.addEventListener('touchstart', onTouch, { passive: true });

    return () => {
      stopHeartbeat();
      if (countRef.current) clearInterval(countRef.current);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('touchstart', onTouch);
    };
    // RPCs derive identity and display name exclusively from the authenticated JWT.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PresenceContext.Provider value={{ onlineCount, onlineUsers: [] }}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence(): PresenceContextValue {
  return useContext(PresenceContext);
}

export async function rawDelete(_userId: string): Promise<void> {
  await supabase.rpc('remove_online_user');
}
